
import React, { useEffect, useMemo, useState } from "react";
import { t, normalizeLang, getLangFromPathname } from "../../i18n";
import { useSiteContent } from "../../content/SiteContentContext";

const COOKIE_FALLBACKS = {
  fi: {
    "cookie.title": "Evästeasetukset",
    "cookie.lead":
      "Käytän analytiikkaa sivuston parantamiseen. Välttämättömät evästeet ovat aina käytössä. Voit hyväksyä analytiikan tai jatkaa vain välttämättömillä.",
    "cookie.onlyNecessary": "Jatka ilman analytiikkaa",
    "cookie.edit": "Asetukset",
    "cookie.acceptSelected": "Hyväksy analytiikka",
    "cookie.settingsTitle": "Evästeasetukset",
    "cookie.necessary.title": "Välttämättömät",
    "cookie.necessary.desc": "Sivusto ei toimi ilman näitä",
    "cookie.analytics.title": "Analytiikka (GA4)",
    "cookie.analytics.desc": "Auttaa ymmärtämään, mitkä sisällöt toimivat",
    "cookie.marketing.title": "Markkinointi",
    "cookie.marketing.desc": "Ei käytössä ilman erillistä tarvetta",
    "cookie.reject": "Hylkää",
    "cookie.save": "Tallenna valinnat",
    "cookie.openSettings": "Evästeet",
    "cookie.aria.necessary": "välttämättömät evästeet",
  },
  en: {
    "cookie.title": "Cookie settings",
    "cookie.lead":
      "I use analytics to improve the website. Necessary cookies are always enabled. You can allow analytics or continue with necessary cookies only.",
    "cookie.onlyNecessary": "Continue without analytics",
    "cookie.edit": "Settings",
    "cookie.acceptSelected": "Allow analytics",
    "cookie.settingsTitle": "Cookie settings",
    "cookie.necessary.title": "Necessary",
    "cookie.necessary.desc": "The website does not work without these",
    "cookie.analytics.title": "Analytics (GA4)",
    "cookie.analytics.desc": "Helps understand which content works",
    "cookie.marketing.title": "Marketing",
    "cookie.marketing.desc": "Not used unless separately needed",
    "cookie.reject": "Reject",
    "cookie.save": "Save choices",
    "cookie.openSettings": "Cookies",
    "cookie.aria.necessary": "necessary cookies",
  },
  es: {
    "cookie.title": "Configuración de cookies",
    "cookie.lead":
      "Uso analítica para mejorar el sitio web. Las cookies necesarias siempre están activas. Puedes permitir la analítica o continuar solo con las necesarias.",
    "cookie.onlyNecessary": "Continuar sin analítica",
    "cookie.edit": "Ajustes",
    "cookie.acceptSelected": "Permitir analítica",
    "cookie.settingsTitle": "Configuración de cookies",
    "cookie.necessary.title": "Necesarias",
    "cookie.necessary.desc": "El sitio no funciona sin estas",
    "cookie.analytics.title": "Analítica (GA4)",
    "cookie.analytics.desc": "Ayuda a entender qué contenidos funcionan",
    "cookie.marketing.title": "Marketing",
    "cookie.marketing.desc": "No se usa salvo necesidad específica",
    "cookie.reject": "Rechazar",
    "cookie.save": "Guardar selección",
    "cookie.openSettings": "Cookies",
    "cookie.aria.necessary": "cookies necesarias",
  },
};

// WordPressin MU-plugin asettaa suostumuksen oletusarvot ennen GA4-tagia.
// React ei aseta uutta default-komentoa, vaan palauttaa tallennetun valinnan.
const DEFAULT_SETTINGS = {
  necessary: true,
  analytics: false,
  marketing: false,
};

const normalizeSettings = (value) => ({
  necessary: true,
  analytics: value?.analytics === true,
  marketing: value?.marketing === true,
});

// Tallennettuun tilaan luotetaan vain, jos käyttäjä on tehnyt valinnan.
// Puuttuva tai virheellinen tieto ei saa tarkoittaa analytiikan hyväksyntää.
const readSavedConsent = () => {
  try {
    const consent = window.localStorage.getItem("cookieConsent");
    const raw = window.localStorage.getItem("cookieSettings");
    const parsed = raw ? JSON.parse(raw) : null;

    if (consent === "rejected") {
      return { consent, settings: { ...DEFAULT_SETTINGS } };
    }

    if (
      consent === "accepted" &&
      parsed &&
      typeof parsed === "object" &&
      typeof parsed.analytics === "boolean" &&
      typeof parsed.marketing === "boolean"
    ) {
      return { consent, settings: normalizeSettings(parsed) };
    }
  } catch {
    // Tallennustila voi olla estetty tai vanha JSON virheellinen.
  }

  return { consent: null, settings: { ...DEFAULT_SETTINGS } };
};

// Käytetään samaa Google Consent Mode -päivitystä kaikissa valinnoissa.
// Jos gtag ei vielä ole olemassa, komento jonotetaan dataLayeriin.
const updateGoogleConsent = (settings) => {
  if (typeof window === "undefined") return;

  if (typeof window.gtag !== "function") {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  window.gtag("consent", "update", {
    analytics_storage: settings.analytics ? "granted" : "denied",
    ad_storage: settings.marketing ? "granted" : "denied",
    ad_user_data: settings.marketing ? "granted" : "denied",
    ad_personalization: settings.marketing ? "granted" : "denied",
  });
};

const CookieConsent = ({ lang: langProp }) => {
  const siteContent = useSiteContent();
  const [showBanner, setShowBanner] = useState(false);
  const [hasChoice, setHasChoice] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });

  const lang = useMemo(() => {
    if (typeof langProp === "string" && langProp.trim()) {
      return normalizeLang(langProp);
    }

    const pathname =
      typeof window !== "undefined" ? window.location.pathname || "/" : "/";

    if (/^\/(en|es)(\/|$)/.test(pathname)) {
      return getLangFromPathname(pathname);
    }

    const htmlLang =
      typeof document !== "undefined"
        ? (document.documentElement.lang || "").trim().slice(0, 2)
        : "";

    if (htmlLang) {
      return normalizeLang(htmlLang);
    }

    return "fi";
  }, [langProp]);

  const tt = (key, fallback) => {
    // siteContent on tarkoituksella luettu hookilla: kun WordPress-sisältö
    // saapuu, komponentti renderöityy uudelleen ja t() käyttää uusia arvoja.
    void siteContent;

    const value = t(key, lang);
    if (typeof value === "string" && value !== key) {
      return value;
    }

    const local = COOKIE_FALLBACKS[lang]?.[key];
    if (typeof local === "string") {
      return local;
    }

    return fallback;
  };

  useEffect(() => {
    const saved = readSavedConsent();

    setSettings(saved.settings);
    setHasChoice(saved.consent !== null);
    setShowBanner(saved.consent === null);

    // WordPressin oletus on jo denied. React palauttaa vain tehdyn
    // valinnan update-komennolla, eikä hyväksy analytiikkaa oletuksena.
    if (saved.consent !== null) {
      updateGoogleConsent(saved.settings);
    }
  }, []);

  const saveConsent = (nextSettings, consent) => {
    const normalized = normalizeSettings(nextSettings);

    // Päivitetään Google-tagille sama valinta, joka tallennetaan.
    updateGoogleConsent(normalized);

    try {
      window.localStorage.setItem("cookieSettings", JSON.stringify(normalized));
      window.localStorage.setItem("cookieConsent", consent);
    } catch {
      // Jos tallennus on estetty, valinta toimii tämän sivulatauksen ajan.
    }

    setSettings(normalized);
    setHasChoice(true);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAccept = () => {
    saveConsent(
      { ...settings, analytics: true },
      "accepted"
    );
  };

  const handleReject = () => {
    saveConsent(DEFAULT_SETTINGS, "rejected");
  };

  const handleSettingsClick = () => {
    setShowSettings(true);
    setShowBanner(true);
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setSettings((prev) => ({ ...prev, [name]: checked }));
  };

  return (
    <>
      {showBanner && (
        <div className="fixed inset-x-0 bottom-0 z-[30000] flex justify-center px-4 pb-4 sm:px-6 sm:pb-6">
          <div
            className="w-full max-w-3xl overflow-hidden rounded-3xl border border-white/15 bg-slate-950/95 p-5 text-white shadow-[0_24px_90px_rgba(0,0,0,0.45)] backdrop-blur-xl sm:p-6"
            role="dialog"
            aria-modal="true"
            aria-label={tt("cookie.title", "Evästeasetukset")}
          >
            {!showSettings ? (
              <>
                <div className="flex items-start gap-4">
                  <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl sm:flex">
                    🍪
                  </div>

                  <div>
                    <h3 className="text-xl font-black text-white">
                      {tt("cookie.title", "Evästeasetukset")}
                    </h3>

                    <p className="mt-2 text-sm leading-relaxed text-gray-300">
                      {tt(
                        "cookie.lead",
                        "Käytän analytiikkaa sivuston parantamiseen. Välttämättömät evästeet ovat aina käytössä. Voit hyväksyä analytiikan tai jatkaa vain välttämättömillä."
                      )}
                    </p>
                  </div>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
                  <button
                    type="button"
                    onClick={handleReject}
                    className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 font-semibold text-white transition hover:bg-white/15"
                  >
                    {tt("cookie.onlyNecessary", "Jatka ilman analytiikkaa")}
                  </button>

                  <button
                    type="button"
                    onClick={handleSettingsClick}
                    className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 font-semibold text-white transition hover:bg-white/10"
                  >
                    {tt("cookie.edit", "Asetukset")}
                  </button>

                  <button
                    type="button"
                    onClick={handleAccept}
                    className="rounded-2xl bg-lime-400 px-5 py-3 font-black text-black shadow-[0_16px_40px_rgba(163,230,53,0.24)] transition hover:bg-lime-300"
                  >
                    {tt("cookie.acceptSelected", "Hyväksy analytiikka")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-xl font-black text-white">
                  {tt("cookie.settingsTitle", "Evästeasetukset")}
                </h3>

                <div className="mt-5 space-y-3 text-sm text-gray-300">
                  <label className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                    <span>
                      <span className="block font-bold text-white">
                        {tt("cookie.necessary.title", "Välttämättömät")}
                      </span>
                      <span className="mt-1 block text-xs text-gray-400">
                        {tt("cookie.necessary.desc", "Sivusto ei toimi ilman näitä")}
                      </span>
                    </span>
                    <input
                      type="checkbox"
                      checked
                      disabled
                      aria-label={tt("cookie.aria.necessary", "välttämättömät evästeet")}
                    />
                  </label>

                  <label className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                    <span>
                      <span className="block font-bold text-white">
                        {tt("cookie.analytics.title", "Analytiikka (GA4)")}
                      </span>
                      <span className="mt-1 block text-xs text-gray-400">
                        {tt("cookie.analytics.desc", "Auttaa ymmärtämään, mitkä sisällöt toimivat")}
                      </span>
                    </span>
                    <input
                      type="checkbox"
                      name="analytics"
                      checked={settings.analytics}
                      onChange={handleCheckboxChange}
                      aria-label={tt("cookie.analytics.title", "Analytiikka (GA4)")}
                    />
                  </label>

                  <label className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                    <span>
                      <span className="block font-bold text-white">
                        {tt("cookie.marketing.title", "Markkinointi")}
                      </span>
                      <span className="mt-1 block text-xs text-gray-400">
                        {tt(
                          "cookie.marketing.desc",
                          "Ei käytössä ilman erillistä tarvetta"
                        )}
                      </span>
                    </span>
                    <input
                      type="checkbox"
                      name="marketing"
                      checked={settings.marketing}
                      onChange={handleCheckboxChange}
                      aria-label={tt("cookie.marketing.title", "Markkinointi")}
                    />
                  </label>
                </div>

                <div className="mt-5 flex justify-between gap-3">
                  <button
                    type="button"
                    onClick={handleReject}
                    className="rounded-2xl border border-white/10 bg-white/10 px-4 py-3 font-semibold text-white transition hover:bg-white/15"
                  >
                    {tt("cookie.reject", "Hylkää")}
                  </button>

                  <button
                    type="button"
                    onClick={() => saveConsent(settings, "accepted")}
                    className="rounded-2xl bg-lime-400 px-5 py-3 font-black text-black shadow-[0_16px_40px_rgba(163,230,53,0.24)] transition hover:bg-lime-300"
                  >
                    {tt("cookie.save", "Tallenna valinnat")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {hasChoice && !showBanner && (
        <button
          type="button"
          onClick={handleSettingsClick}
          className="fixed bottom-5 left-5 z-30 rounded-full border border-white/10 bg-slate-950/85 px-4 py-2 text-xs font-bold text-white shadow-xl backdrop-blur transition hover:bg-slate-900"
        >
          {tt("cookie.openSettings", "Evästeet")}
        </button>
      )}
    </>
  );
};

export default CookieConsent;